import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import {
  isPublicAppRoute,
  LOCAL_VAR,
  PUBLIC_API_PATHS
} from '@app/core/constants';
import { ErrorMessageService } from '@app/core/service';
import { UtilService } from '@app/core/service/util.service';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

/**
 * Handles API validation/display errors, and forces logout only on
 * auth failures (401/403) for authenticated sessions (skipping public
 * auth pages/APIs). Other errors (500, timeout, network) stay signed in.
 */
@Injectable()
export class ErrorHandlerInterceptor implements HttpInterceptor {
  private isLoggingOut = false;

  constructor(
    private router: Router,
    private injector: Injector,
    private utilService: UtilService,
    private errorMessageService: ErrorMessageService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    this.utilService.clearMissedError();
    return next
      .handle(request)
      .pipe(catchError(error => this.errorHandler(error, request)));
  }

  private errorHandler(
    response: HttpErrorResponse,
    request: HttpRequest<any>
  ): Observable<HttpEvent<any>> {
    this.applyDisplayErrors(response);

    const authService = this.injector.get(AuthenticationService);
    const onPublicPage = isPublicAppRoute(this.router.url);
    const publicApi =
      this.isPublicApi(request.url) || this.isPublicApi(response.url || '');

    // Only log out on auth failures — not on 500/timeout/network errors
    const isAuthFailure =
      response.status === 401 || response.status === 403;
    if (
      !this.isLoggingOut &&
      isAuthFailure &&
      authService.isAuthenticated() &&
      !onPublicPage &&
      !publicApi
    ) {
      this.isLoggingOut = true;
      localStorage.removeItem(LOCAL_VAR.selectedAgent);
      authService.logout(true).subscribe(() => {
        this.router
          .navigate(['/login'], {
            replaceUrl: true
          })
          .then(() => {
            this.isLoggingOut = false;
          });
      });
    }

    throw response;
  }

  private applyDisplayErrors(response: HttpErrorResponse): void {
    const errorResponse: any = response;
    if (response.status === 400) {
      if (errorResponse.error) {
        if (
          errorResponse.error.validation &&
          errorResponse.error.validation.errors
        ) {
          this.errorMessageService.clear();
          Object.keys(errorResponse.error.validation.errors).forEach(
            (key: string) => {
              this.errorMessageService.set(
                errorResponse.error.validation.errors[key],
                key,
                response.url
              );
            }
          );
        } else if (errorResponse.error.message) {
          this.utilService.clearMissedError();
          this.utilService.setMissedError(errorResponse.error.message);
        }
      }
    } else if (response.status !== 401 && response.status !== 403) {
      const message =
        (errorResponse.error && errorResponse.error.message) ||
        (response.status === 0
          ? 'Request failed. Please check your connection and try again.'
          : null);
      if (message) {
        this.utilService.clearMissedError();
        this.utilService.setMissedError(message);
      }
    }
  }

  private isPublicApi(url: string): boolean {
    if (!url) {
      return false;
    }
    return PUBLIC_API_PATHS.some(path => url.includes(path));
  }
}
